import { useEffect, useCallback, useRef, useMemo } from 'react';
import { AlertTriangle, ChevronLeft, Folder, Loader2, X } from 'lucide-react';
import { Button, Drawer } from '@aimsos/ui';
import { getShellMainContentEl } from '../../../shared/utils/shellPortal.js';
import { useSlideoutAgentDrives } from '../hooks/knowledge/useSlideoutAgentDrives';

/**
 * Slide-out: browse MANAGE vaults, select drives/docs, grant READER to the agent.
 */
export default function KnowledgeBrowseDrivesDrawer({ isOpen, onClose, t, toast }) {
  const slide = useSlideoutAgentDrives();
  const fetchDrivesRef = useRef(slide.fetchDrives);
  const drawerPortalTarget = useMemo(
    () => (isOpen ? getShellMainContentEl() : null),
    [isOpen],
  );

  useEffect(() => {
    fetchDrivesRef.current = slide.fetchDrives;
  }, [slide.fetchDrives]);

  useEffect(() => {
    if (!isOpen) return;
    void fetchDrivesRef.current(true);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    slide.resetState();
    onClose?.();
  }, [onClose, slide]);

  const handleGrant = useCallback(async () => {
    const result = await slide.grantAccess();
    if (result.success) {
      toast.success({ title: t('edit.knowledge.browse.granted'), canClose: true });
      handleClose();
    } else {
      toast.error({
        title: t('edit.knowledge.toastErrorTitle'),
        message: result.errors?.join('\n') ?? t('edit.knowledge.browse.grantFailed'),
        canClose: true,
      });
    }
  }, [slide, toast, t, handleClose]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      position="right"
      portalTarget={drawerPortalTarget ?? undefined}
      ariaLabel={t('edit.knowledge.browse.title')}
      panelClassName="bg-surface"
    >
      <div className="flex h-full min-h-0 flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-2 border-b border-edge pb-3">
          <h2 className="text-base font-semibold text-content">{t('edit.knowledge.browse.title')}</h2>
          <button
            type="button"
            className="rounded-md p-1 text-content-muted hover:bg-surface-hover-subtle hover:text-content"
            aria-label={t('edit.knowledge.browse.close')}
            onClick={handleClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {slide.isDocumentsView && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"
            onClick={slide.handleBackToList}
          >
            <ChevronLeft className="h-4 w-4" />
            {t('edit.knowledge.browse.back')}
          </button>
        )}

        <input
          type="search"
          value={slide.searchQuery}
          onChange={(e) => slide.setSearchQuery(e.target.value)}
          placeholder={t('edit.knowledge.browse.searchPlaceholder')}
          className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-content placeholder:text-content-faint focus:outline-none focus:ring-2 focus:ring-brand-500"
        />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {slide.drivesLoading || slide.documentsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-content-muted" />
            </div>
          ) : slide.drivesError && !slide.isDocumentsView ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              <p className="text-sm text-content-muted">
                {slide.drivesError?.message ?? t('edit.knowledge.browse.loadError')}
              </p>
              <button
                type="button"
                className="mt-1 text-xs text-brand-600 hover:underline"
                onClick={() => void slide.fetchDrives(true)}
              >
                {t('edit.knowledge.browse.retry')}
              </button>
            </div>
          ) : slide.isDocumentsView ? (
            slide.documentsError ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <p className="text-sm text-content-muted">
                  {slide.documentsError?.message ?? t('edit.knowledge.browse.loadError')}
                </p>
                <button
                  type="button"
                  className="mt-1 text-xs text-brand-600 hover:underline"
                  onClick={() => void slide.fetchDriveDocuments(slide.selectedDrive, true)}
                >
                  {t('edit.knowledge.browse.retry')}
                </button>
              </div>
            ) : (
            <ul className="flex flex-col gap-2">
              {slide.driveDocuments.map((doc) => {
                const id = doc.uuid || doc.id;
                const checked = slide.selectedDocumentIds.has(id);
                return (
                  <li key={id}>
                    <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-edge p-3 hover:bg-surface-hover-subtle">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => slide.toggleDocumentSelection(id)}
                        className="mt-1"
                      />
                      <span className="text-sm text-content">{doc.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            )
          ) : (
            <ul className="flex flex-col gap-2">
              {slide.drives.map((drive) => {
                const id = drive.vaultUuid || drive.uuid || drive.id;
                const checked = slide.selectedDriveIds.has(id);
                return (
                  <li key={id}>
                    <div className="flex items-stretch gap-2 rounded-lg border border-edge">
                      <label className="flex flex-1 cursor-pointer items-center gap-2 p-3 hover:bg-surface-hover-subtle">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => slide.toggleDriveSelection(id)}
                        />
                        <Folder className="h-4 w-4 shrink-0 text-content-muted" />
                        <span className="text-sm font-medium text-content">{drive.name || id}</span>
                      </label>
                      <button
                        type="button"
                        className="border-l border-edge px-3 text-xs text-brand-600 hover:bg-surface-hover-subtle"
                        onClick={() => void slide.fetchDriveDocuments(drive)}
                      >
                        {t('edit.knowledge.browse.open')}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-edge pt-3">
          <span className="text-xs text-content-muted">
            {t('edit.knowledge.browse.selected', { count: slide.selectedCount })}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={handleClose}>
              {t('edit.knowledge.browse.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={slide.selectedCount === 0 || slide.isAssigning}
              onClick={() => void handleGrant()}
            >
              {slide.isAssigning ? t('edit.knowledge.browse.assigning') : t('edit.knowledge.browse.grant')}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
